import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supportTicketsAPI } from '../../services/portalAPI';
import PortalHelpGuides from './PortalHelpGuides';

interface Ticket {
  id: number;
  subject: string;
  status: string;
  priority: string;
  category: string;
  created_at: string;
  resolved_at: string | null;
}

const statusColors: Record<string, { bg: string; color: string }> = {
  open: { bg: '#e3f2fd', color: '#1565c0' },
  in_progress: { bg: '#fff8e1', color: '#f57f17' },
  resolved: { bg: '#e8f5e9', color: '#2e7d32' },
  closed: { bg: '#f5f5f5', color: '#757575' },
};

const statusLabels: Record<string, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  closed: 'Closed',
};

const PortalSupportTickets: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'guides' ? 'guides' : 'tickets';
  const [activeTab, setActiveTab] = useState<'guides' | 'tickets'>(initialTab);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTickets();
  }, []);

  const handleTabChange = (tab: 'guides' | 'tickets') => {
    setActiveTab(tab);
    setSearchParams(tab === 'guides' ? { tab: 'guides' } : {});
  };

  const loadTickets = async () => {
    try {
      const res = await supportTicketsAPI.getAll();
      setTickets(res.data);
    } catch (err) {
      console.error('Failed to load tickets', err);
    } finally {
      setLoading(false);
    }
  };

  const tabStyle = (isActive: boolean): React.CSSProperties => ({
    padding: '10px 20px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    borderBottom: isActive ? '2px solid #6C8062' : '2px solid transparent',
    background: 'none',
    color: isActive ? '#111827' : '#6b7280',
    minHeight: 44,
  });

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: '#111827' }}>Help & Support</h1>
        {activeTab === 'tickets' && (
          <button
            onClick={() => navigate('/portal/support/new')}
            style={{
              background: '#6C8062', color: '#fff', border: 'none', borderRadius: 6,
              padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
              minHeight: 44,
            }}
          >
            New Ticket
          </button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', marginBottom: 20, gap: 4 }}>
        <button style={tabStyle(activeTab === 'guides')} onClick={() => handleTabChange('guides')}>
          Help & Guides
        </button>
        <button style={tabStyle(activeTab === 'tickets')} onClick={() => handleTabChange('tickets')}>
          Support Tickets
        </button>
      </div>

      {/* Guides Tab */}
      {activeTab === 'guides' && <PortalHelpGuides embedded />}

      {/* Tickets Tab */}
      {activeTab === 'tickets' && (
        <>
          {loading ? (
            <p style={{ color: '#888' }}>Loading tickets...</p>
          ) : tickets.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 48, color: '#888' }}>
              <p style={{ fontSize: 16, marginBottom: 8 }}>No support tickets yet.</p>
              <p style={{ fontSize: 14 }}>Click "New Ticket" to create your first support request.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {tickets.map(ticket => {
                const sc = statusColors[ticket.status] || statusColors.open;
                return (
                  <div
                    key={ticket.id}
                    onClick={() => navigate(`/portal/support/${ticket.id}`)}
                    style={{
                      background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8,
                      padding: 16, cursor: 'pointer', transition: 'box-shadow 0.15s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)')}
                    onMouseLeave={e => (e.currentTarget.style.boxShadow = 'none')}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 600, fontSize: 15 }}>{ticket.subject}</span>
                          <span style={{
                            background: sc.bg, color: sc.color, fontSize: 11, fontWeight: 600,
                            padding: '2px 8px', borderRadius: 10,
                          }}>
                            {statusLabels[ticket.status] || ticket.status}
                          </span>
                        </div>
                        <div style={{ color: '#888', fontSize: 13 }}>
                          #{ticket.id} &middot; {ticket.category.replace('_', ' ')} &middot; {new Date(ticket.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <span style={{ color: '#bbb', fontSize: 20 }}>&rsaquo;</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PortalSupportTickets;
